/**
 * metrics – every performance figure LearnXR shows, defined once.
 *
 * Pure functions over plain records, no Firestore, so each definition is
 * tested once (tests/analyticsMetrics.test.mts) and a student, their teacher
 * and their principal can never be shown two different numbers for one thing.
 *
 * Definitions
 *   percentage   marks obtained / maximum marks × 100. Unanswered questions
 *                earn no marks, so they count against it.
 *   accuracy     correct / attempted × 100. Null when nothing was attempted.
 *   completion   attempted / total questions × 100.
 *   valid        a submission with at least one question attempted. Only valid
 *                submissions enter averages, medians, highs, lows and pass
 *                rates; an empty one would drag every one of them to zero.
 *   average      mean of submission percentages — never of raw marks, since
 *                quizzes differ in length, and never a mean of class means.
 *   one per student
 *                figures for a single lesson or session use each student's
 *                latest attempt, so a student who retook a quiz three times
 *                is not counted three times.
 *   improvement  latest percentage − previous percentage, in percentage
 *                points. 60% → 75% is +15 pp, not +25%.
 *
 * Every function returns null rather than NaN when there is nothing to
 * measure, and formatPercent renders null as "—".
 */

export const DEFAULT_PASS_THRESHOLD = 70;

/** A Firestore Timestamp, a serialised one, an ISO string, a Date or epoch ms. */
export type TimeLike =
  | { toMillis(): number }
  | { seconds: number; nanoseconds?: number }
  | { _seconds: number }
  | string
  | number
  | Date
  | null
  | undefined;

export function toMillis(value: TimeLike): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof (value as any).toMillis === 'function') return (value as any).toMillis();
  if (typeof (value as any).seconds === 'number') return (value as any).seconds * 1000;
  if (typeof (value as any)._seconds === 'number') return (value as any)._seconds * 1000;
  return null;
}

// ---------------------------------------------------------------------------
// One result
// ---------------------------------------------------------------------------

export interface ResponseRecord {
  question_id: string;
  question_text?: string;
  options?: string[];
  student_answer_index: number | null;
  correct_answer_index: number;
  is_correct: boolean;
  time_spent_seconds?: number | null;
}

/** A student_scores document as stored — any generation of it. */
export interface RawScore {
  id?: string;
  student_id?: string;
  student_name?: string;
  school_id?: string;
  class_id?: string | null;
  class_label?: string | null;
  teacher_id?: string | null;
  session_id?: string | null;
  chapter_id?: string;
  topic_id?: string;
  lesson_name?: string | null;
  chapter_name?: string | null;
  subject?: string;
  attempt_number?: number;
  score?: { correct: number; total: number; percentage?: number };
  questions_attempted?: number;
  completed?: boolean;
  total_questions?: number;
  attempted_questions?: number;
  correct_answers?: number;
  marks_obtained?: number;
  maximum_marks?: number;
  responses?: ResponseRecord[];
  answers?: Record<string, number>;
  completed_at?: TimeLike;
  started_at?: TimeLike;
  time_taken_seconds?: number | null;
  completion_time_seconds?: number | null;
}

/** A result with every figure present, whichever generation it was stored in. */
export interface Result {
  id: string;
  studentId: string;
  studentName: string | null;
  schoolId: string | null;
  classId: string | null;
  classLabel: string | null;
  teacherId: string | null;
  sessionId: string | null;
  chapterId: string;
  topicId: string;
  /** chapter + topic: one lesson. */
  lessonKey: string;
  lessonName: string | null;
  chapterName: string | null;
  subject: string | null;
  attempt: number;
  completedAt: number | null;
  startedAt: number | null;
  totalQuestions: number;
  attempted: number;
  correct: number;
  incorrect: number;
  unanswered: number;
  marksObtained: number;
  maximumMarks: number;
  percentage: number;
  accuracy: number | null;
  completion: number;
  status: 'completed' | 'partial';
  timeSeconds: number | null;
  responses: ResponseRecord[];
  /** Question id → chosen option, as older records stored answers. */
  answers: Record<string, number>;
}

const round1 = (n: number) => Math.round(n * 10) / 10;
const nonNegInt = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.round(v) : null;

/** part / whole × 100 to one decimal, or null when there is no whole. */
export function ratio(part: number, whole: number): number | null {
  return whole > 0 ? round1((part / whole) * 100) : null;
}

/**
 * Read any stored score as a complete Result.
 *
 * Server-graded scores carry every field. Older ones carry `score` and
 * sometimes `questions_attempted`; everything else is derived from those two
 * with the same definitions, so a legacy score and a new one are comparable.
 */
export function normaliseScore(raw: RawScore): Result {
  const total = nonNegInt(raw.total_questions) ?? nonNegInt(raw.score?.total) ?? 0;
  const correct = Math.min(nonNegInt(raw.correct_answers) ?? nonNegInt(raw.score?.correct) ?? 0, total);
  const attempted = Math.max(
    correct,
    Math.min(nonNegInt(raw.attempted_questions) ?? nonNegInt(raw.questions_attempted) ?? total, total)
  );
  const maximumMarks = nonNegInt(raw.maximum_marks) ?? total;
  const marksObtained = Math.min(nonNegInt(raw.marks_obtained) ?? correct, maximumMarks);
  const chapterId = raw.chapter_id ?? '';
  const topicId = raw.topic_id ?? '';

  return {
    id: raw.id ?? '',
    studentId: raw.student_id ?? '',
    studentName: raw.student_name || null,
    schoolId: raw.school_id ?? null,
    classId: raw.class_id ?? null,
    classLabel: raw.class_label ?? null,
    teacherId: raw.teacher_id ?? null,
    sessionId: raw.session_id ?? null,
    chapterId,
    topicId,
    lessonKey: `${chapterId}::${topicId}`,
    lessonName: raw.lesson_name || null,
    chapterName: raw.chapter_name || null,
    subject: raw.subject || null,
    attempt: nonNegInt(raw.attempt_number) || 1,
    completedAt: toMillis(raw.completed_at),
    startedAt: toMillis(raw.started_at),
    totalQuestions: total,
    attempted,
    correct,
    incorrect: attempted - correct,
    unanswered: total - attempted,
    marksObtained,
    maximumMarks,
    percentage: ratio(marksObtained, maximumMarks) ?? 0,
    accuracy: ratio(correct, attempted),
    completion: ratio(attempted, total) ?? 0,
    status: total > 0 && attempted >= total && raw.completed !== false ? 'completed' : 'partial',
    timeSeconds: nonNegInt(raw.completion_time_seconds) ?? nonNegInt(raw.time_taken_seconds),
    responses: Array.isArray(raw.responses) ? raw.responses : [],
    answers: raw.answers && typeof raw.answers === 'object' ? raw.answers : {},
  };
}

/** At least one question attempted, out of a quiz that had marks to give. */
export const isValid = (r: Result) => r.attempted > 0 && r.maximumMarks > 0;

/** A readable name for a lesson whose record predates stored names. */
export function lessonTitle(r: Pick<Result, 'lessonName' | 'chapterName' | 'topicId' | 'chapterId'>): string {
  return r.lessonName || r.chapterName || r.topicId || r.chapterId || 'Untitled lesson';
}

// ---------------------------------------------------------------------------
// Statistics
// ---------------------------------------------------------------------------

export function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  return round1(values.reduce((s, v) => s + v, 0) / values.length);
}

/** The true median: the middle value, or the mean of the two middle values. */
export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return round1(sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2);
}

/** Current minus previous, in percentage points. Null when either is missing. */
export function improvementPp(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  return round1(current - previous);
}

/** Chronological order, oldest first. Undated records sort first. */
export const byTime = (a: Result, b: Result) =>
  (a.completedAt ?? 0) - (b.completedAt ?? 0) || a.attempt - b.attempt;

/**
 * One result per student per lesson: their latest attempt.
 *
 * Used for any figure about a single lesson or session, so a retake replaces
 * a student's earlier result rather than counting them twice.
 */
export function latestPerStudent(results: Result[]): Result[] {
  const latest = new Map<string, Result>();
  for (const r of results) {
    const key = `${r.studentId}|${r.lessonKey}`;
    const current = latest.get(key);
    if (!current || byTime(current, r) < 0) latest.set(key, r);
  }
  return [...latest.values()];
}

export interface Summary {
  /** Every submission given, valid or not. */
  submissions: number;
  /** Submissions with at least one question attempted. */
  validSubmissions: number;
  students: number;
  average: number | null;
  median: number | null;
  highest: Result | null;
  lowest: Result | null;
  /** Share of valid submissions at or above the pass threshold. */
  passRate: number | null;
  passThreshold: number;
  /** Mean of per-submission accuracy, over submissions that attempted anything. */
  accuracy: number | null;
  /** Mean completion over every submission, including empty ones. */
  completion: number | null;
  /** Sum of recorded completion times, over submissions that recorded one. */
  totalTimeSeconds: number;
}

export function summarise(results: Result[], passThreshold = DEFAULT_PASS_THRESHOLD): Summary {
  const valid = results.filter(isValid);
  const percentages = valid.map((r) => r.percentage);
  const ranked = [...valid].sort((a, b) => b.percentage - a.percentage);
  const accuracies = valid.map((r) => r.accuracy).filter((a): a is number => a !== null);

  return {
    submissions: results.length,
    validSubmissions: valid.length,
    students: new Set(results.map((r) => r.studentId)).size,
    average: mean(percentages),
    median: median(percentages),
    highest: ranked[0] ?? null,
    lowest: ranked[ranked.length - 1] ?? null,
    passRate: ratio(valid.filter((r) => r.percentage >= passThreshold).length, valid.length),
    passThreshold,
    accuracy: mean(accuracies),
    completion: mean(results.map((r) => r.completion)),
    totalTimeSeconds: results.reduce((s, r) => s + (r.timeSeconds ?? 0), 0),
  };
}

export const SCORE_BANDS = [
  { label: '0–20', min: 0, max: 20 },
  { label: '21–40', min: 21, max: 40 },
  { label: '41–60', min: 41, max: 60 },
  { label: '61–80', min: 61, max: 80 },
  { label: '81–100', min: 81, max: 100 },
] as const;

/** How many percentages fall in each band, after rounding. */
export function bandCounts(percentages: number[]): { label: string; count: number }[] {
  const counts = SCORE_BANDS.map((b) => ({ label: b.label, count: 0 }));
  for (const value of percentages) {
    const pct = Math.round(value);
    const index = SCORE_BANDS.findIndex((b) => pct >= b.min && pct <= b.max);
    if (index >= 0) counts[index].count++;
  }
  return counts;
}

/** How many valid results fall in each band. */
export function distribution(results: Result[]): { label: string; count: number }[] {
  return bandCounts(results.filter(isValid).map((r) => r.percentage));
}

// ---------------------------------------------------------------------------
// Groupings
// ---------------------------------------------------------------------------

export interface TrendPoint {
  /** Start of the day, epoch ms. */
  date: number;
  average: number;
  count: number;
}

/** Average percentage per calendar day (local time), oldest first. */
export function dailyTrend(results: Result[]): TrendPoint[] {
  const days = new Map<number, number[]>();
  for (const r of results.filter(isValid)) {
    if (r.completedAt === null) continue;
    const d = new Date(r.completedAt);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    if (!days.has(day)) days.set(day, []);
    days.get(day)!.push(r.percentage);
  }
  return [...days.entries()]
    .sort(([a], [b]) => a - b)
    .map(([date, values]) => ({ date, average: mean(values)!, count: values.length }));
}

export interface StudentPerformance {
  studentId: string;
  studentName: string | null;
  classId: string | null;
  submissions: number;
  /** Distinct lessons with at least one valid result. */
  lessonsCompleted: number;
  average: number | null;
  accuracy: number | null;
  completion: number | null;
  latest: Result | null;
  /** Latest result minus the one before it, in pp. */
  improvement: number | null;
  /** Percentages oldest first, for a sparkline. */
  trend: number[];
  totalTimeSeconds: number;
}

export function studentPerformance(results: Result[], passThreshold = DEFAULT_PASS_THRESHOLD): StudentPerformance[] {
  const byStudent = new Map<string, Result[]>();
  for (const r of results) {
    if (!byStudent.has(r.studentId)) byStudent.set(r.studentId, []);
    byStudent.get(r.studentId)!.push(r);
  }

  return [...byStudent.entries()].map(([studentId, list]) => {
    const ordered = [...list].sort(byTime);
    const valid = ordered.filter(isValid);
    const summary = summarise(list, passThreshold);
    const latest = valid[valid.length - 1] ?? null;
    const previous = valid[valid.length - 2] ?? null;
    return {
      studentId,
      studentName: ordered.map((r) => r.studentName).filter(Boolean).pop() ?? null,
      classId: ordered[ordered.length - 1]?.classId ?? null,
      submissions: list.length,
      lessonsCompleted: new Set(valid.map((r) => r.lessonKey)).size,
      average: summary.average,
      accuracy: summary.accuracy,
      completion: summary.completion,
      latest,
      improvement: improvementPp(latest?.percentage, previous?.percentage),
      trend: valid.map((r) => r.percentage),
      totalTimeSeconds: summary.totalTimeSeconds,
    };
  });
}

export interface GroupPerformance {
  key: string;
  label: string;
  summary: Summary;
  lastActivity: number | null;
}

/**
 * Summaries grouped by any key — lesson, subject, class, chapter.
 *
 * `perStudentLatest` counts each student once per lesson within the group,
 * which is what a lesson's figures should mean; leave it off for groups that
 * span time, where every attempt is a separate piece of evidence.
 */
export function groupPerformance(
  results: Result[],
  keyOf: (r: Result) => string | null,
  labelOf: (r: Result) => string,
  { passThreshold = DEFAULT_PASS_THRESHOLD, perStudentLatest = false } = {}
): GroupPerformance[] {
  const groups = new Map<string, Result[]>();
  for (const r of results) {
    const key = keyOf(r);
    if (key === null) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }
  return [...groups.entries()].map(([key, list]) => {
    const counted = perStudentLatest ? latestPerStudent(list) : list;
    const times = list.map((r) => r.completedAt).filter((t): t is number => t !== null);
    return {
      key,
      label: labelOf([...list].sort(byTime)[list.length - 1]),
      summary: summarise(counted, passThreshold),
      lastActivity: times.length ? Math.max(...times) : null,
    };
  });
}

export interface QuestionStat {
  questionId: string;
  order: number;
  text: string;
  options: string[];
  correctIndex: number;
  correct: number;
  incorrect: number;
  unanswered: number;
  /** correct / (correct + incorrect). Null when no one answered it. */
  accuracy: number | null;
  /** How many students chose each option. */
  optionCounts: number[];
  /** Most-chosen wrong option, when one stands out. */
  commonWrongIndex: number | null;
}

/** A question worth a teacher's attention: under half right, with enough answers to mean it. */
export const isDifficult = (q: QuestionStat, minimumAnswers = 3) =>
  q.accuracy !== null && q.accuracy < 50 && q.correct + q.incorrect >= minimumAnswers;

/**
 * Per-question figures across a set of results.
 *
 * Only server-graded results carry per-question responses; older ones are
 * skipped, and `coverage` says how many results the figures rest on.
 */
export function questionStats(results: Result[]): { questions: QuestionStat[]; coverage: number } {
  const stats = new Map<string, QuestionStat>();
  let coverage = 0;
  for (const r of results) {
    if (r.responses.length === 0) continue;
    coverage++;
    r.responses.forEach((resp, order) => {
      let q = stats.get(resp.question_id);
      if (!q) {
        const options = resp.options ?? [];
        q = {
          questionId: resp.question_id,
          order,
          text: resp.question_text || `Question ${order + 1}`,
          options,
          correctIndex: resp.correct_answer_index,
          correct: 0,
          incorrect: 0,
          unanswered: 0,
          accuracy: null,
          optionCounts: options.map(() => 0),
          commonWrongIndex: null,
        };
        stats.set(resp.question_id, q);
      }
      if (resp.student_answer_index === null) q.unanswered++;
      else {
        if (resp.is_correct) q.correct++;
        else q.incorrect++;
        if (resp.student_answer_index < q.optionCounts.length) q.optionCounts[resp.student_answer_index]++;
      }
    });
  }
  const questions = [...stats.values()].sort((a, b) => a.order - b.order);
  for (const q of questions) {
    q.accuracy = ratio(q.correct, q.correct + q.incorrect);
    let best = -1;
    q.optionCounts.forEach((count, i) => {
      if (i !== q.correctIndex && count > 0 && (best < 0 || count > q.optionCounts[best])) best = i;
    });
    q.commonWrongIndex = best >= 0 ? best : null;
  }
  return { questions, coverage };
}

/**
 * Recent direction: the mean of the latest results against the mean of the
 * same number just before them, in pp. Up to five a side, and at least two a
 * side, so one lucky quiz can't read as a trend.
 */
export function recentTrend(results: Result[], window = 5): { change: number; count: number } | null {
  const valid = results.filter(isValid).sort(byTime);
  const count = Math.min(window, Math.floor(valid.length / 2));
  if (count < 2) return null;
  const latest = valid.slice(-count).map((r) => r.percentage);
  const before = valid.slice(-2 * count, -count).map((r) => r.percentage);
  return { change: improvementPp(mean(latest), mean(before))!, count };
}

export interface StrengthArea {
  key: string;
  label: string;
  average: number;
  submissions: number;
}

/**
 * Areas going well and areas needing attention, by a stated rule rather than
 * a judgement: a group with at least `minimum` valid results averaging 75% or
 * more is a strength, under 50% needs attention. Groups with fewer results say
 * nothing either way.
 */
export function strengthsAndGaps(
  results: Result[],
  keyOf: (r: Result) => string | null,
  labelOf: (r: Result) => string,
  { minimum = 2, strong = 75, weak = 50 } = {}
): { strengths: StrengthArea[]; gaps: StrengthArea[] } {
  const groups = groupPerformance(results.filter(isValid), keyOf, labelOf);
  const areas = groups
    .filter((g) => g.summary.validSubmissions >= minimum && g.summary.average !== null)
    .map((g) => ({ key: g.key, label: g.label, average: g.summary.average!, submissions: g.summary.validSubmissions }));
  return {
    strengths: areas.filter((a) => a.average >= strong).sort((a, b) => b.average - a.average),
    gaps: areas.filter((a) => a.average < weak).sort((a, b) => a.average - b.average),
  };
}

// ---------------------------------------------------------------------------
// Periods
// ---------------------------------------------------------------------------

export type RangePreset = '7d' | '30d' | 'year' | 'all' | 'custom';

export interface DateRange {
  from: number | null;
  to: number | null;
}

/** Indian school year: 1 April to 31 March. */
export function academicYearStart(now: number): number {
  const d = new Date(now);
  const year = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return new Date(year, 3, 1).getTime();
}

const DAY = 24 * 60 * 60 * 1000;

export function rangeForPreset(preset: RangePreset, now = Date.now(), custom?: DateRange): DateRange {
  switch (preset) {
    case '7d':
      return { from: now - 7 * DAY, to: now };
    case '30d':
      return { from: now - 30 * DAY, to: now };
    case 'year':
      return { from: academicYearStart(now), to: now };
    case 'custom':
      return custom ?? { from: null, to: null };
    default:
      return { from: null, to: null };
  }
}

/** The equally long period immediately before, or null for an open range. */
export function previousRange(range: DateRange): DateRange | null {
  if (range.from === null || range.to === null) return null;
  const length = range.to - range.from;
  return { from: range.from - length, to: range.from };
}

/** Whether a moment falls in [from, to). An undated moment is in only an open range. */
export const withinRange = (ms: number | null, range: DateRange | null) =>
  !range ||
  ((range.from === null || (ms !== null && ms >= range.from)) && (range.to === null || (ms !== null && ms < range.to)));

export const inRange = (r: Result, range: DateRange | null) => withinRange(r.completedAt, range);

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

const finite = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isFinite(n);

export const formatPercent = (n: number | null | undefined) => (finite(n) ? `${Math.round(n)}%` : '—');

export const formatPp = (n: number | null | undefined) =>
  finite(n) ? `${n > 0 ? '+' : n < 0 ? '−' : '±'}${Math.abs(Math.round(n))} pp` : '—';

export function formatDuration(seconds: number | null | undefined): string {
  if (!finite(seconds) || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, '0')}s`;
  return `${s}s`;
}

/** Tailwind text colour for a score, matching the convention used across dashboards. */
export function scoreTone(pct: number | null | undefined, passThreshold = DEFAULT_PASS_THRESHOLD): string {
  if (!finite(pct)) return 'text-muted-foreground';
  if (pct >= passThreshold) return 'text-primary';
  if (pct >= 50) return 'text-amber-500';
  return 'text-destructive';
}
